// src/components/ResourceManager/Hardware/request/request.ts

import axios from "axios";
const API_URL = process.env.REACT_APP_API_URL || "";

/** Interfaz del payload al crear/editar hardware. */
export interface AddHardwareResourcePayload {
	datacenterId: string;
	typeHardware: string | { name: string } | null;
    subtypeHardware: string | { name: string } | null;
	serialNumber: string;
	model: string;
	vendor: string;
	hostname: string;
	installedDate: string;
	endOfLifeDate: string;
	status: string;
	rackId?: string;
	rackUnit?: string;
	phase?: string;
	template?: string;
	targetDeviceId?: number| null; // If "module" needs to reference the target hardware
	slotAssignmentId?: number| null;
}

/** Interfaz DataCenter */
export interface DataCenterItem {
	datacenterId: string;
	name: string;
}

/** Interfaz principal Hardware */
export interface Hardware {
	id: number;
	datacenterId: string;
	name: string;
	typeHardware: string | { name: string } | null;
	subtypeHardware: string | { name: string } | null;
	serialNumber: string;
	model: string;
	vendor: string;
	hostname: string;
	installedDate: string;
	endOfLifeDate: string;
	phase: string;
	rackId: string;
	rackUnit?: number | string | null;
	status: string;
	template?: string;
	targetDeviceId?: number| null; // If "module" needs to reference the target hardware
	slotAssignmentId?: number| null;
}

/**
 * Interfaz "Port".
 * Todo en string para evitar problemas con “string|number”.
 * isNew / isEditing para lógica local.
 */
export interface Port {
	id: string;
	portName: string;
	portNumber: string;
	portType: string;
	portStatus: string;
	isNew?: boolean;
	isEditing?: boolean;
}
export interface HardwareType {
	id: number;
	name: string;
}
export interface HardwareSubtype {
	id: number;
	name: string;
}

/** GET /v1/dataCenters/ids-names */
export function getDataCentersIdsNames(token: string) {
	return axios.get<DataCenterItem[]>(`${API_URL}/v1/dataCenters/ids-names`, {
		headers: { Authorization: `Bearer ${token}` },
	});
}

/** GET /v1/hardwareResources/datacenter/filter?:datacenterId */
export function getHardwareByDatacenterId(token: string, datacenterId: string) {
	const encodedId = encodeURIComponent(datacenterId);
	return axios.get<Hardware[]>(
		`${API_URL}/v1/hardwareResources/filter?datacenterId=${encodedId}`,
		{
			headers: { Authorization: `Bearer ${token}` },
		}
	);
}

/** POST /v1/hardwareResources/create-by-template */
export function addHardwareResource(
	token: string,
	payload: AddHardwareResourcePayload
) {
	return axios.post(
		`${API_URL}/v1/hardwareResources/add`,
		payload,
		{
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		}
	);
}

/** PUT /v1/hardwareResources/:id */
export function updateHardwareResource(
	token: string,
	resourceId: string,
	payload: AddHardwareResourcePayload
) {
	return axios.put(`${API_URL}/v1/hardwareResources/${resourceId}`, payload, {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	});
}

/** DELETE /v1/hardwareResources/:id */
export function deleteHardwareResource(token: string, resourceId: string) {
	return axios.delete(`${API_URL}/v1/hardwareResources/${resourceId}`, {
		headers: { Authorization: `Bearer ${token}` },
	});
}

/** GET /v1/port/hardware/:hardwareId/ports */
export function getPortsByHardwareId(token: string, hardwareId: string) {
	return axios.get(`${API_URL}/v1/port/hardware/${hardwareId}/ports`, {
		headers: { Authorization: `Bearer ${token}` },
	});
}

/** Payload para crear un puerto (POST /v1/port). */
export interface CreatePortPayload {
	hardwareResourceId: number;
	portName: string;
	portId: string;
	portNumber: string;
	portType: string;
	portStatus: string;
	l2Description?: string;
	l3Description?: string;
	clientAssignation?: string;
}

/** POST /v1/port */
export function createPort(token: string, payload: CreatePortPayload) {
	return axios.post(`${API_URL}/v1/port`, payload, {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	});
}

/** Payload para actualizar un puerto (PUT /v1/port/:id). */
export interface UpdatePortPayload {
	portName: string;
	portId: string;
	portNumber: string;
	portType: string;
	portStatus: string;
	l2Description?: string;
	l3Description?: string;
	clientAssignation?: string;
}

/** PUT /v1/port/:id */
export function updatePort(
	token: string,
	portId: string,
	payload: UpdatePortPayload
) {
	return axios.put(`${API_URL}/v1/port/${portId}`, payload, {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	});
}

/** DELETE /v1/port/:id */
export function deletePort(token: string, portId: string) {
	return axios.delete(`${API_URL}/v1/port/${portId}`, {
		headers: { Authorization: `Bearer ${token}` },
	});
}

// -- Nueva función para GET /v1/typeHardware
export function getHardwareTypes(token: string) {
	return axios.get<HardwareType[]>(`${API_URL}/v1/typeHardware`, {
		headers: { Authorization: `Bearer ${token}` },
	});
}

export function getSubTypesByTypeHardware(
	token: string,
	typeHardwareId: number
) {
	return axios.get<HardwareSubtype[]>(
		`${API_URL}/v1/typeHardware/${typeHardwareId}/subtypes`,
		{ headers: { Authorization: `Bearer ${token}` } }
	);
}
